// Basic
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
//  Store and actions
import usePlansStore from '@/app/store/modules/plan';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useSessionStore from '@/app/store/session';
// Components
import { Tabs } from '@/common-utils/components/ui/tabs';
import Page from '@/common-utils/components/structure/page/Page';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import PlansTable from '@/app/modules/plans/components/tables/PlansTable';
import ModalCreatePlan from '@/app/modules/plans/components/modals/createPlan/ModalCreatePlan';
import ModalBlockNewPos from '@/app/modules/plans/components/modals/blockNewPos/ModalBlockNewPos';
import Info from '@/common-utils/components/renders/info/Info';
// Common
import RouteUrls from '../enums/RouteUrls';
import ICONS from '@/common-utils/enums/icons';
import { buildUrl, replaceParameters } from '@/common-utils/functions/restUtils';
import ORGANIZATION_DETAILS_TABS from '../enums/tabs/OrganizationDetailsTabs';
import DETAILS_TAB_SECTIONS from '@/app/modules/organizations/enums/sections/DETAILS_TAB_SECTIONS';
import Permissions from '@/app/permissions/Permissions';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
// Lang
import { nsPlan } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @returns {JSX} Plans
 */
function Plans() {

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const shouldBlockNewPOS = usePlansStore(s => s.shouldBlockNewPOS);
    const session = useSessionStore(s => s.session);
    const permissions = session.permissions;

    // State
    const [showModalCreatePlan, setShowModalCreatePlan] = useState(false);
    const [showModalBlockNewPos, setShowModalBlockNewPos] = useState(false);

    const navigate = useNavigate();

    // Functions
    const handleReceiveAlertsRedirection = () => {
        const organizationUrl = buildUrl(
            replaceParameters(RouteUrls.ORGANIZATION_DETAILS.url, { organizationId: rootOrganization.uuid }),
            { view: ORGANIZATION_DETAILS_TABS.DETAILS.name, section: DETAILS_TAB_SECTIONS.ALERTS }
        );
        navigate(organizationUrl);
    };

    return (
        <Page>
            <Tabs defaultValue='plans'>
                <Page.Header
                    route={RouteUrls.PLANS}
                    routeBase={RouteUrls.PLANS}
                />
                <Page.Body dataCy='page-plans'>
                    <>

                        {shouldBlockNewPOS &&
                            <Info
                                entity='info.block_new_pos'
                                namespace={nsPlan}
                                icon={ICONS.INFO}
                                className='justify-center'
                                variant='danger'
                            />
                        }
                        <PlansTable
                            filtersEventName={setFiltersEventName('plans')}
                            pageActions={
                                <PageActions actions={[
                                    {
                                        icon: ICONS.ADD,
                                        entity: 'actions.page.create_plan',
                                        namespace: nsPlan,
                                        onClick: () => setShowModalCreatePlan(true),
                                        visible: true,
                                        disabled: !hasRequiredPermissions({
                                            permissions: permissions,
                                            requiredPermissions: [Permissions.SUBSCRIPTION_PLAN_CREATE.name, Permissions.SUBSCRIPTION_PLAN_LIST_PROVIDER_PLANS.name]
                                        }),
                                        tooltipForbidden: 'title.permissions.access_denied'
                                    },
                                    {
                                        icon: ICONS.ALERTS,
                                        entity: 'actions.page.receive_alerts',
                                        namespace: nsPlan,
                                        onClick: handleReceiveAlertsRedirection,
                                        visible: true,
                                        disabled: !hasRequiredPermissions({
                                            permissions: permissions,
                                            requiredPermissions: [Permissions.ORGANIZATION_GET_ALERT_POS.name]
                                        }),
                                        tooltipForbidden: 'title.permissions.access_denied'
                                    },
                                    {
                                        icon: shouldBlockNewPOS ? ICONS.POS_TERMINAL_UNBLOCK : ICONS.POS_TERMINAL_BLOCK,
                                        variant: shouldBlockNewPOS ? 'secondary' : 'critical',
                                        entity: shouldBlockNewPOS ? 'actions.page.new_pos.unblock' : 'actions.page.new_pos.block',
                                        namespace: nsPlan,
                                        onClick: () => setShowModalBlockNewPos(true),
                                        visible: true,
                                        disabled: !hasRequiredPermissions({
                                            permissions: permissions,
                                            requiredPermissions: [Permissions.ORGANIZATION_UPDATE_SHOULD_BLOCK_NEW_POS.name]
                                        }),
                                        tooltipForbidden: 'title.permissions.access_denied'
                                    }
                                ]}
                                    retractable
                                />
                            } />
                    </>
                </Page.Body>
            </Tabs>
            {showModalCreatePlan &&
                <ModalCreatePlan
                    isOpen={showModalCreatePlan}
                    setIsOpen={setShowModalCreatePlan}
                />
            }
            {showModalBlockNewPos &&
                <ModalBlockNewPos
                    isOpen={showModalBlockNewPos}
                    setIsOpen={setShowModalBlockNewPos}
                />
            }
        </Page>
    );
}

export default Plans;